import { StateCreator } from "zustand";
import { VendState } from ".";
import {
  createInventoryItem as createInventoryItemApi,
  createMachineInventoryItem as createMachineInventoryItemApi,
  createProduct as createProductApi,
  deleteInventoryItem as deleteInventoryItemApi,
  deleteMachineInventoryItem as deleteMachineInventoryItemApi,
  editInventoryItem as editInventoryItemApi,
  editMachineInventoryItem as editMachineInventoryItemApi,
  editProduct as editProductApi,
  fetchAllMachineInventoryItems,
  fetchAllInventoryMovements,
  fetchInventories,
  fetchInventoryVans,
  fetchMachineInventoryItems,
  fetchMachineInventoryMovements,
  fetchMachineInventoryMovementsForMachine,
  fetchProducts,
  InventoryItemPayload,
  InventoryItemStockPayload,
  MachineInventoryItemPayload,
  MachineInventoryStockPayload,
  ProductPayload,
  updateInventoryItemStock as updateInventoryItemStockApi,
  updateMachineInventoryStock as updateMachineInventoryStockApi,
} from "../api/inventory/api";
import {
  Inventory,
  InventoryVan,
  InventoryItem,
  InventoryMovement,
  MachineInventoryItem,
  Product,
} from "../types";
import { withWriteAction } from "../utils";

const DEFAULT_TTL_MS = 5 * 60 * 1000;

const productsInFlight = new Map<string, Promise<void>>();
const vansInFlight = new Map<string, Promise<void>>();
const inventoriesInFlight = new Map<string, Promise<void>>();
const machineInventoryInFlight = new Map<string, Promise<void>>();
const allMachineInventoryInFlight = new Map<string, Promise<void>>();
const allInventoryMovementsInFlight = new Map<string, Promise<void>>();
const movementsInFlight = new Map<string, Promise<void>>();
const machineMovementsInFlight = new Map<string, Promise<void>>();
let allMachineInventoryFetchedAt = 0;
let allInventoryMovementsFetchedAt = 0;
let inventoryLoadGeneration = 0;

export const resetInventoryLoadState = () => {
  inventoryLoadGeneration += 1;
  productsInFlight.clear();
  inventoriesInFlight.clear();
  vansInFlight.clear();
  machineInventoryInFlight.clear();
  allMachineInventoryInFlight.clear();
  allInventoryMovementsInFlight.clear();
  movementsInFlight.clear();
  machineMovementsInFlight.clear();
  allMachineInventoryFetchedAt = 0;
  allInventoryMovementsFetchedAt = 0;
};

const isFresh = (fetchedAt?: number | null, ttlMs = DEFAULT_TTL_MS) =>
  Boolean(fetchedAt && Date.now() - fetchedAt <= ttlMs);

type InventoryLoadOptions = { force?: boolean; ttlMs?: number; silent?: boolean };

const mergeProducts = (
  existingById: Record<string, Product>,
  existingIds: string[],
  products: Product[],
) => {
  const byId = { ...existingById };
  const ids = new Set(existingIds);
  products.forEach((product) => {
    if (!product.id) return;
    byId[product.id] = product;
    ids.add(product.id);
  });
  return { byId, ids: Array.from(ids).sort((a, b) => (byId[a]?.name || "").localeCompare(byId[b]?.name || "")) };
};

const mergeMachineInventoryItems = (
  current: VendState,
  machineId: string,
  items: MachineInventoryItem[],
) => {
  const itemIds = items.map((item) => item.id).filter(Boolean);
  const nextItemsById = { ...current.machineInventoryItemsById };
  items.forEach((item) => {
    if (item.id) nextItemsById[item.id] = item;
  });
  return {
    machineInventoryItemsById: nextItemsById,
    machineInventoryItemIdsByMachineId: {
      ...current.machineInventoryItemIdsByMachineId,
      [machineId]: itemIds,
    },
    machineInventoryFetchedAtByMachineId: {
      ...current.machineInventoryFetchedAtByMachineId,
      [machineId]: Date.now(),
    },
  };
};

const upsertMachineInventoryItem = (
  current: VendState,
  item: MachineInventoryItem,
) => {
  const machineId = item.machineId;
  const currentIds = current.machineInventoryItemIdsByMachineId[machineId] || [];
  const ids = currentIds.includes(item.id) ? currentIds : [...currentIds, item.id];
  return {
    machineInventoryItemsById: {
      ...current.machineInventoryItemsById,
      [item.id]: item,
    },
    machineInventoryItemIdsByMachineId: {
      ...current.machineInventoryItemIdsByMachineId,
      [machineId]: ids,
    },
    machineInventoryFetchedAtByMachineId: {
      ...current.machineInventoryFetchedAtByMachineId,
      [machineId]: Date.now(),
    },
  };
};

const upsertInventoryItem = (
  current: VendState,
  item: InventoryItem,
) => {
  const inventoryId = item.inventoryId;
  const currentIds = current.inventoryItemIdsByInventoryId[inventoryId] || [];
  const ids = currentIds.includes(item.id) ? currentIds : [...currentIds, item.id];
  return {
    inventoryItemsById: {
      ...current.inventoryItemsById,
      [item.id]: item,
    },
    inventoryItemIdsByInventoryId: {
      ...current.inventoryItemIdsByInventoryId,
      [inventoryId]: ids,
    },
    inventoriesFetchedAt: Date.now(),
  };
};

export type InventorySlice = {
  productsById: Record<string, Product>;
  productIds: string[];
  productsHydrated: boolean;
  productsLoading: boolean;
  productsFetchedAt: number | null;
  inventoryVans: InventoryVan[];
  inventoryVansLoading: boolean;
  inventoryVansFetchedAt: number | null;
  loadInventoryVans: (options?: InventoryLoadOptions) => Promise<void>;
  inventoriesById: Record<string, Inventory>;
  inventoryIds: string[];
  inventoryItemsById: Record<string, InventoryItem>;
  inventoryItemIdsByInventoryId: Record<string, string[]>;
  inventoriesHydrated: boolean;
  inventoriesLoading: boolean;
  inventoriesFetchedAt: number | null;
  machineInventoryItemsById: Record<string, MachineInventoryItem>;
  machineInventoryItemIdsByMachineId: Record<string, string[]>;
  machineInventoryLoadingByMachineId: Record<string, boolean>;
  machineInventoryFetchedAtByMachineId: Record<string, number>;
  inventoryMovementsById: Record<string, InventoryMovement>;
  machineInventoryMovementIdsByItemId: Record<string, string[]>;
  machineInventoryMovementsLoadingByItemId: Record<string, boolean>;
  machineInventoryMovementsFetchedAtByItemId: Record<string, number>;
  setProducts: (products: Product[]) => void;
  loadProducts: (options?: InventoryLoadOptions) => Promise<void>;
  createProduct: (payload: ProductPayload) => Promise<Product>;
  editProduct: (id: string, payload: Partial<ProductPayload>) => Promise<Product>;
  loadInventories: (options?: InventoryLoadOptions) => Promise<void>;
  createInventoryItem: (
    inventoryId: string,
    payload: InventoryItemPayload,
  ) => Promise<InventoryItem>;
  editInventoryItem: (
    id: string,
    payload: Partial<Omit<InventoryItemPayload, "productId">>,
  ) => Promise<void>;
  deleteInventoryItem: (id: string) => Promise<void>;
  updateInventoryItemStock: (
    id: string,
    payload: InventoryItemStockPayload,
  ) => Promise<InventoryItem>;
  loadMachineInventory: (
    machineId: string,
    options?: InventoryLoadOptions,
  ) => Promise<void>;
  loadAllMachineInventory: (options?: InventoryLoadOptions) => Promise<void>;
  createMachineInventoryItem: (
    machineId: string,
    payload: MachineInventoryItemPayload,
  ) => Promise<MachineInventoryItem>;
  editMachineInventoryItem: (
    id: string,
    payload: Partial<MachineInventoryItemPayload>,
  ) => Promise<void>;
  deleteMachineInventoryItem: (id: string) => Promise<void>;
  updateMachineInventoryStock: (
    id: string,
    payload: MachineInventoryStockPayload,
  ) => Promise<MachineInventoryItem>;
  loadMachineInventoryMovements: (
    itemId: string,
    options?: InventoryLoadOptions,
  ) => Promise<void>;
  loadMachineInventoryMovementsForMachine: (
    machineId: string,
    itemIds: string[],
    options?: InventoryLoadOptions,
  ) => Promise<void>;
  loadAllInventoryMovements: (options?: InventoryLoadOptions) => Promise<void>;
  refreshInventoryFromRealtime: (event?: {
    machineId?: string | null;
    inventoryId?: string | null;
    itemId?: string | null;
    productId?: string | null;
  }) => Promise<void>;
};

export const createInventorySlice: StateCreator<
  VendState,
  [],
  [],
  InventorySlice
> = (set, get) => ({
  productsById: {},
  productIds: [],
  productsHydrated: false,
  productsLoading: false,
  productsFetchedAt: null,
  inventoryVans: [],
  inventoryVansLoading: false,
  inventoryVansFetchedAt: null,
  loadInventoryVans: async (options) => {
    const state = get() as VendState;
    if (!options?.force && isFresh(state.inventoryVansFetchedAt, options?.ttlMs)) return;
    const key = "vans";
    const pending = vansInFlight.get(key);
    if (pending && !options?.force) return pending;
    if (!options?.silent || !state.inventoryVansFetchedAt) set({ inventoryVansLoading: true } as VendState);
    const request = fetchInventoryVans().then((inventoryVans) => {
      if (vansInFlight.get(key) !== request) return;
      set({ inventoryVans, inventoryVansFetchedAt: Date.now() } as VendState);
    }).finally(() => {
      if (vansInFlight.get(key) !== request) return;
      set({ inventoryVansLoading: false } as VendState);
      vansInFlight.delete(key);
    });
    vansInFlight.set(key, request);
    return request;
  },
  inventoriesById: {},
  inventoryIds: [],
  inventoryItemsById: {},
  inventoryItemIdsByInventoryId: {},
  inventoriesHydrated: false,
  inventoriesLoading: false,
  inventoriesFetchedAt: null,
  machineInventoryItemsById: {},
  machineInventoryItemIdsByMachineId: {},
  machineInventoryLoadingByMachineId: {},
  machineInventoryFetchedAtByMachineId: {},
  inventoryMovementsById: {},
  machineInventoryMovementIdsByItemId: {},
  machineInventoryMovementsLoadingByItemId: {},
  machineInventoryMovementsFetchedAtByItemId: {},
  setProducts: (products) => {
    set((state) => {
      const merged = mergeProducts((state as VendState).productsById, (state as VendState).productIds, products);
      return {
        ...(state as VendState),
        productsById: merged.byId,
        productIds: merged.ids,
        productsHydrated: true,
        productsFetchedAt: Date.now(),
      };
    });
  },
  loadProducts: async (options) => {
    const state = get() as VendState;
    if (!options?.force && state.productsHydrated && isFresh(state.productsFetchedAt, options?.ttlMs)) return;
    const key = "products";
    const inFlight = productsInFlight.get(key);
    if (!options?.force && inFlight) return inFlight;
    const showLoading = !options?.silent || !state.productsHydrated;
    if (showLoading) set({ productsLoading: true } as VendState);
    const request = fetchProducts()
      .then((products) => {
        if (productsInFlight.get(key) !== request) return;
        get().setProducts(products);
      })
      .finally(() => {
        if (productsInFlight.get(key) !== request) return;
        if (showLoading) set({ productsLoading: false } as VendState);
        productsInFlight.delete(key);
      });
    productsInFlight.set(key, request);
    return request;
  },
  createProduct: async (payload) => {
    return withWriteAction("Creating Product", async () => {
      const generation = inventoryLoadGeneration;
      const product = await createProductApi(payload);
      if (generation === inventoryLoadGeneration) get().setProducts([product]);
      return product;
    });
  },
  editProduct: async (id, payload) => {
    return withWriteAction("Updating Product", async () => {
      const generation = inventoryLoadGeneration;
      const product = await editProductApi(id, payload);
      if (generation === inventoryLoadGeneration) get().setProducts([product]);
      return product;
    });
  },
  loadInventories: async (options) => {
    const state = get() as VendState;
    if (!options?.force && state.inventoriesHydrated && isFresh(state.inventoriesFetchedAt, options?.ttlMs)) return;
    const key = "inventories";
    const inFlight = inventoriesInFlight.get(key);
    if (!options?.force && inFlight) return inFlight;
    const showLoading = !options?.silent || !state.inventoriesHydrated;
    if (showLoading) set({ inventoriesLoading: true } as VendState);
    const request = fetchInventories()
      .then(({ inventories, inventoryItems, products }) => {
        if (inventoriesInFlight.get(key) !== request) return;
        set((current) => {
          const currentState = current as VendState;
          const mergedProducts = mergeProducts(currentState.productsById, currentState.productIds, products);
          // This endpoint returns a full snapshot; merging would retain deleted stock.
          const inventoriesById: Record<string, Inventory> = {};
          const inventoryIds = new Set<string>();
          const inventoryItemsById: Record<string, InventoryItem> = {};
          const itemIdsByInventory: Record<string, string[]> = {};
          inventories.forEach((inventory) => {
            inventoriesById[inventory.id] = inventory;
            inventoryIds.add(inventory.id);
            itemIdsByInventory[inventory.id] = [];
          });
          inventoryItems.forEach((item) => {
            inventoryItemsById[item.id] = item;
            itemIdsByInventory[item.inventoryId] = itemIdsByInventory[item.inventoryId] || [];
            itemIdsByInventory[item.inventoryId].push(item.id);
          });
          return {
            ...currentState,
            productsById: mergedProducts.byId,
            productIds: mergedProducts.ids,
            inventoriesById,
            inventoryIds: Array.from(inventoryIds),
            inventoryItemsById,
            inventoryItemIdsByInventoryId: itemIdsByInventory,
            inventoriesHydrated: true,
            inventoriesFetchedAt: Date.now(),
          };
        });
      })
      .finally(() => {
        if (inventoriesInFlight.get(key) !== request) return;
        if (showLoading) set({ inventoriesLoading: false } as VendState);
        inventoriesInFlight.delete(key);
      });
    inventoriesInFlight.set(key, request);
    return request;
  },
  createInventoryItem: async (inventoryId, payload) => {
    return withWriteAction("Creating Warehouse Stock", async () => {
      const { item, product } = await createInventoryItemApi(inventoryId, payload);
      set((current) => {
        const currentState = current as VendState;
        const mergedProducts = product
          ? mergeProducts(currentState.productsById, currentState.productIds, [product])
          : { byId: currentState.productsById, ids: currentState.productIds };
        return {
          ...currentState,
          productsById: mergedProducts.byId,
          productIds: mergedProducts.ids,
          ...upsertInventoryItem(currentState, item),
        };
      });
      return item;
    });
  },
  editInventoryItem: async (id, payload) => {
    return withWriteAction("Saving Stock", async () => {
      await editInventoryItemApi(id, payload);
      await get().loadInventories({ force: true, silent: true });
    });
  },
  deleteInventoryItem: async (id) => {
    return withWriteAction("Deleting Stock", async () => {
      const existing = (get() as VendState).inventoryItemsById[id];
      await deleteInventoryItemApi(id);
      set((current) => {
        const currentState = current as VendState;
        const { [id]: _removedItem, ...inventoryItemsById } = currentState.inventoryItemsById;
        const { [id]: _removedMovementIds, ...machineInventoryMovementIdsByItemId } = currentState.machineInventoryMovementIdsByItemId;
        const nextIdsByInventory = { ...currentState.inventoryItemIdsByInventoryId };
        if (existing?.inventoryId) {
          nextIdsByInventory[existing.inventoryId] = (
            nextIdsByInventory[existing.inventoryId] || []
          ).filter((itemId) => itemId !== id);
        }
        return {
          ...currentState,
          inventoryItemsById,
          inventoryItemIdsByInventoryId: nextIdsByInventory,
          machineInventoryMovementIdsByItemId,
          inventoriesFetchedAt: Date.now(),
        };
      });
    });
  },
  updateInventoryItemStock: async (id, payload) => {
    return withWriteAction("Updating Stock", async () => {
      const { item, product } = await updateInventoryItemStockApi(id, payload);
      const existing = (get() as VendState).inventoryItemsById[id];
      const normalizedItem = item.inventoryId ? item : { ...item, inventoryId: existing?.inventoryId || "" };
      set((current) => {
        const currentState = current as VendState;
        const mergedProducts = product
          ? mergeProducts(currentState.productsById, currentState.productIds, [product])
          : { byId: currentState.productsById, ids: currentState.productIds };
        return {
          ...currentState,
          productsById: mergedProducts.byId,
          productIds: mergedProducts.ids,
          ...upsertInventoryItem(currentState, normalizedItem),
        };
      });
      return normalizedItem;
    });
  },
  loadMachineInventory: async (machineId, options) => {
    if (!machineId) return;
    const state = get() as VendState;
    const fetchedAt = state.machineInventoryFetchedAtByMachineId[machineId];
    if (!options?.force && state.machineInventoryItemIdsByMachineId[machineId] && isFresh(fetchedAt, options?.ttlMs)) return;
    const inFlight = machineInventoryInFlight.get(machineId);
    if (!options?.force && inFlight) return inFlight;
    const showLoading = !options?.silent || !state.machineInventoryItemIdsByMachineId[machineId];
    if (showLoading) {
      set((current) => ({
        ...(current as VendState),
        machineInventoryLoadingByMachineId: {
          ...(current as VendState).machineInventoryLoadingByMachineId,
          [machineId]: true,
        },
      }));
    }
    const request = fetchMachineInventoryItems(machineId)
      .then(({ items, products }) => {
        set((current) => {
          const currentState = current as VendState;
          const mergedProducts = mergeProducts(currentState.productsById, currentState.productIds, products);
          return {
            ...currentState,
            productsById: mergedProducts.byId,
            productIds: mergedProducts.ids,
            ...mergeMachineInventoryItems(currentState, machineId, items),
          };
        });
      })
      .finally(() => {
        if (showLoading) {
          set((current) => ({
            ...(current as VendState),
            machineInventoryLoadingByMachineId: {
              ...(current as VendState).machineInventoryLoadingByMachineId,
              [machineId]: false,
            },
          }));
        }
        machineInventoryInFlight.delete(machineId);
      });
    machineInventoryInFlight.set(machineId, request);
    return request;
  },
  loadAllMachineInventory: async (options) => {
    if (!options?.force && isFresh(allMachineInventoryFetchedAt, options?.ttlMs)) return;
    const key = "all";
    const inFlight = allMachineInventoryInFlight.get(key);
    if (!options?.force && inFlight) return inFlight;
    const request = fetchAllMachineInventoryItems()
      .then(({ items, products }) => {
        set((current) => {
          const currentState = current as VendState;
          const mergedProducts = mergeProducts(currentState.productsById, currentState.productIds, products);
          const nextItemsById = { ...currentState.machineInventoryItemsById };
          const itemIdsByMachine: Record<string, string[]> = {};
          const fetchedAtByMachine = { ...currentState.machineInventoryFetchedAtByMachineId };
          const now = Date.now();
          items.forEach((item) => {
            nextItemsById[item.id] = item;
            itemIdsByMachine[item.machineId] = itemIdsByMachine[item.machineId] || [];
            itemIdsByMachine[item.machineId].push(item.id);
            fetchedAtByMachine[item.machineId] = now;
          });
          return {
            ...currentState,
            productsById: mergedProducts.byId,
            productIds: mergedProducts.ids,
            machineInventoryItemsById: nextItemsById,
            machineInventoryItemIdsByMachineId: {
              ...currentState.machineInventoryItemIdsByMachineId,
              ...itemIdsByMachine,
            },
            machineInventoryFetchedAtByMachineId: fetchedAtByMachine,
          };
        });
        allMachineInventoryFetchedAt = Date.now();
      })
      .finally(() => {
        allMachineInventoryInFlight.delete(key);
      });
    allMachineInventoryInFlight.set(key, request);
    return request;
  },
  createMachineInventoryItem: async (machineId, payload) => {
    return withWriteAction("Creating Inventory Item", async () => {
      const { item, product } = await createMachineInventoryItemApi(machineId, payload);
      set((current) => {
        const currentState = current as VendState;
        const mergedProducts = product
          ? mergeProducts(currentState.productsById, currentState.productIds, [product])
          : { byId: currentState.productsById, ids: currentState.productIds };
        return {
          ...currentState,
          productsById: mergedProducts.byId,
          productIds: mergedProducts.ids,
          ...upsertMachineInventoryItem(currentState, item),
        };
      });
      allMachineInventoryFetchedAt = 0;
      return item;
    });
  },
  editMachineInventoryItem: async (id, payload) => {
    return withWriteAction("Saving Inventory Item", async () => {
      const existing = (get() as VendState).machineInventoryItemsById[id];
      await editMachineInventoryItemApi(id, payload);
      if (existing?.machineId) {
        await get().loadMachineInventory(existing.machineId, { force: true, silent: true });
      }
      allMachineInventoryFetchedAt = 0;
    });
  },
  deleteMachineInventoryItem: async (id) => {
    return withWriteAction("Deleting Inventory Item", async () => {
      const existing = (get() as VendState).machineInventoryItemsById[id];
      await deleteMachineInventoryItemApi(id);
      set((current) => {
        const currentState = current as VendState;
        const { [id]: _removedItem, ...machineInventoryItemsById } = currentState.machineInventoryItemsById;
        const { [id]: _removedMovementIds, ...machineInventoryMovementIdsByItemId } = currentState.machineInventoryMovementIdsByItemId;
        const nextIdsByMachine = { ...currentState.machineInventoryItemIdsByMachineId };
        if (existing?.machineId) {
          nextIdsByMachine[existing.machineId] = (nextIdsByMachine[existing.machineId] || []).filter((itemId) => itemId !== id);
        }
        return {
          ...currentState,
          machineInventoryItemsById,
          machineInventoryItemIdsByMachineId: nextIdsByMachine,
          machineInventoryMovementIdsByItemId,
        };
      });
      allMachineInventoryFetchedAt = 0;
      allInventoryMovementsFetchedAt = 0;
    });
  },
  updateMachineInventoryStock: async (id, payload) => {
    return withWriteAction("Updating Stock", async () => {
      const { item, product } = await updateMachineInventoryStockApi(id, payload);
      set((current) => {
        const currentState = current as VendState;
        const mergedProducts = product
          ? mergeProducts(currentState.productsById, currentState.productIds, [product])
          : { byId: currentState.productsById, ids: currentState.productIds };
        return {
          ...currentState,
          productsById: mergedProducts.byId,
          productIds: mergedProducts.ids,
          ...upsertMachineInventoryItem(currentState, item),
          machineInventoryMovementsFetchedAtByItemId: {
            ...currentState.machineInventoryMovementsFetchedAtByItemId,
            [id]: 0,
          },
        };
      });
      allMachineInventoryFetchedAt = 0;
      allInventoryMovementsFetchedAt = 0;
      if ((get() as VendState).machineInventoryMovementIdsByItemId[id]) {
        get().loadMachineInventoryMovements(id, { force: true, silent: true }).catch(() => {});
      }
      return item;
    });
  },
  loadMachineInventoryMovements: async (itemId, options) => {
    if (!itemId) return;
    const state = get() as VendState;
    const fetchedAt = state.machineInventoryMovementsFetchedAtByItemId[itemId];
    if (!options?.force && state.machineInventoryMovementIdsByItemId[itemId] && isFresh(fetchedAt, options?.ttlMs)) return;
    const inFlight = movementsInFlight.get(itemId);
    if (!options?.force && inFlight) return inFlight;
    const showLoading = !options?.silent || !state.machineInventoryMovementIdsByItemId[itemId];
    if (showLoading) {
      set((current) => ({
        ...(current as VendState),
        machineInventoryMovementsLoadingByItemId: {
          ...(current as VendState).machineInventoryMovementsLoadingByItemId,
          [itemId]: true,
        },
      }));
    }
    const request = fetchMachineInventoryMovements(itemId)
      .then((movements) => {
        set((current) => {
          const currentState = current as VendState;
          const byId = { ...currentState.inventoryMovementsById };
          const ids = movements.map((movement) => {
            byId[movement.id] = movement;
            return movement.id;
          });
          return {
            ...currentState,
            inventoryMovementsById: byId,
            machineInventoryMovementIdsByItemId: {
              ...currentState.machineInventoryMovementIdsByItemId,
              [itemId]: ids,
            },
            machineInventoryMovementsFetchedAtByItemId: {
              ...currentState.machineInventoryMovementsFetchedAtByItemId,
              [itemId]: Date.now(),
            },
          };
        });
      })
      .finally(() => {
        if (showLoading) {
          set((current) => ({
            ...(current as VendState),
            machineInventoryMovementsLoadingByItemId: {
              ...(current as VendState).machineInventoryMovementsLoadingByItemId,
              [itemId]: false,
            },
          }));
        }
        movementsInFlight.delete(itemId);
      });
    movementsInFlight.set(itemId, request);
    return request;
  },
  loadMachineInventoryMovementsForMachine: async (machineId, itemIds, options) => {
    if (!machineId || itemIds.length === 0) return;
    const state = get() as VendState;
    const shouldSkip =
      !options?.force &&
      itemIds.every(
        (itemId) =>
          state.machineInventoryMovementIdsByItemId[itemId] &&
          isFresh(state.machineInventoryMovementsFetchedAtByItemId[itemId], options?.ttlMs),
      );
    if (shouldSkip) return;
    const inFlight = machineMovementsInFlight.get(machineId);
    if (!options?.force && inFlight) return inFlight;
    const itemIdSet = new Set(itemIds);
    const showLoading = !options?.silent || itemIds.some(
      (itemId) => !state.machineInventoryMovementIdsByItemId[itemId],
    );
    if (showLoading) {
      set((current) => {
        const currentState = current as VendState;
        const loadingByItem = { ...currentState.machineInventoryMovementsLoadingByItemId };
        itemIds.forEach((itemId) => {
          loadingByItem[itemId] = true;
        });
        return {
          ...currentState,
          machineInventoryMovementsLoadingByItemId: loadingByItem,
        };
      });
    }
    const request = fetchMachineInventoryMovementsForMachine(machineId)
      .then((movements) => {
        set((current) => {
          const currentState = current as VendState;
          const byId = { ...currentState.inventoryMovementsById };
          const idsByItem: Record<string, string[]> = {};
          itemIds.forEach((itemId) => {
            idsByItem[itemId] = [];
          });
          movements.forEach((movement) => {
            byId[movement.id] = movement;
            const itemId = movement.machineInventoryItem || "";
            if (!itemIdSet.has(itemId)) return;
            idsByItem[itemId] = idsByItem[itemId] || [];
            idsByItem[itemId].push(movement.id);
          });
          const fetchedAtByItem = {
            ...currentState.machineInventoryMovementsFetchedAtByItemId,
          };
          itemIds.forEach((itemId) => {
            fetchedAtByItem[itemId] = Date.now();
          });
          return {
            ...currentState,
            inventoryMovementsById: byId,
            machineInventoryMovementIdsByItemId: {
              ...currentState.machineInventoryMovementIdsByItemId,
              ...idsByItem,
            },
            machineInventoryMovementsFetchedAtByItemId: fetchedAtByItem,
          };
        });
      })
      .finally(() => {
        if (showLoading) {
          set((current) => {
            const currentState = current as VendState;
            const loadingByItem = { ...currentState.machineInventoryMovementsLoadingByItemId };
            itemIds.forEach((itemId) => {
              loadingByItem[itemId] = false;
            });
            return {
              ...currentState,
              machineInventoryMovementsLoadingByItemId: loadingByItem,
            };
          });
        }
        machineMovementsInFlight.delete(machineId);
      });
    machineMovementsInFlight.set(machineId, request);
    return request;
  },
  loadAllInventoryMovements: async (options) => {
    if (!options?.force && isFresh(allInventoryMovementsFetchedAt, options?.ttlMs)) return;
    const key = "all";
    const inFlight = allInventoryMovementsInFlight.get(key);
    if (!options?.force && inFlight) return inFlight;
    const request = fetchAllInventoryMovements()
      .then((movements) => {
        set((current) => {
          const currentState = current as VendState;
          const byId = { ...currentState.inventoryMovementsById };
          const idsByItem: Record<string, string[]> = {};
          Object.keys(currentState.machineInventoryItemsById).forEach((itemId) => {
            idsByItem[itemId] = [];
          });
          movements.forEach((movement) => {
            byId[movement.id] = movement;
            const itemId = movement.machineInventoryItem || "";
            if (!itemId) return;
            idsByItem[itemId] = idsByItem[itemId] || [];
            idsByItem[itemId].push(movement.id);
          });
          const fetchedAtByItem = {
            ...currentState.machineInventoryMovementsFetchedAtByItemId,
          };
          const now = Date.now();
          Object.keys(idsByItem).forEach((itemId) => {
            fetchedAtByItem[itemId] = now;
          });
          return {
            ...currentState,
            inventoryMovementsById: byId,
            machineInventoryMovementIdsByItemId: {
              ...currentState.machineInventoryMovementIdsByItemId,
              ...idsByItem,
            },
            machineInventoryMovementsFetchedAtByItemId: fetchedAtByItem,
          };
        });
        allInventoryMovementsFetchedAt = Date.now();
      })
      .finally(() => {
        allInventoryMovementsInFlight.delete(key);
      });
    allInventoryMovementsInFlight.set(key, request);
    return request;
  },
  refreshInventoryFromRealtime: async (event) => {
    const reloads: Promise<void>[] = [];
    const shouldRefreshLoadedMovements =
      allInventoryMovementsFetchedAt > 0 || allInventoryMovementsInFlight.has("all");
    allInventoryMovementsFetchedAt = 0;
    if (event?.machineId) {
      allMachineInventoryFetchedAt = 0;
      reloads.push(get().loadMachineInventory(event.machineId, { force: true, silent: true }));
    }
    if (event?.inventoryId) {
      reloads.push(get().loadInventories({ force: true, silent: true }));
    }
    if (event?.itemId) {
      allInventoryMovementsFetchedAt = 0;
      reloads.push(get().loadMachineInventoryMovements(event.itemId, { force: true, silent: true }));
    }
    if (event?.productId || (!event?.machineId && !event?.inventoryId && !event?.itemId)) {
      reloads.push(get().loadProducts({ force: true, silent: true }));
    }
    if (shouldRefreshLoadedMovements) {
      reloads.push(get().loadAllInventoryMovements({ force: true, silent: true }));
    }
    await Promise.allSettled(reloads);
  },
});
